import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { StatCard, Card, CardTitle, Badge, Btn, Avatar } from '../components/UI';

const STATUS_CONF = {
  Delivered:         { type:'success', label:'Delivered' },
  Pending:           { type:'warning', label:'Pending' },
  'Out for Delivery':{ type:'info',    label:'In Transit' },
  Cancelled:         { type:'danger',  label:'Cancelled' },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]     = useState(null);
  const [bookings, setBookings] = useState([]);
  const [rebook, setRebook]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getStats(), api.getBookings({limit:7}), api.getRebookEligible()])
      .then(([s,b,r]) => {
        setStats(s.data);
        setBookings(b.data.bookings || []);
        setRebook(r.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div>
      {/* Greeting */}
      <div className="au" style={{ marginBottom:26 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <Avatar name={user?.name} size={46} />
          <div>
            <div style={{ fontSize:12.5, color:'var(--text-muted)', fontWeight:500 }}>{greeting} 👋</div>
            <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text-primary)', letterSpacing:'-.025em', lineHeight:1.2 }}>{user?.name}</h1>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
          {[...Array(4)].map((_,i) => (
            <div key={i} className="skeleton" style={{ height:106, borderRadius:'var(--radius-lg)' }} />
          ))}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:26 }}>
          <StatCard label="Total Customers" value={stats?.totalCustomers??0} icon="👥"
            sub={`${stats?.activeCustomers??0} active`} onClick={() => navigate('/customers')} />
          <StatCard label="Total Bookings" value={stats?.total??0} icon="📋"
            sub={`${stats?.pending??0} pending`} onClick={() => navigate('/bookings')} />
          <StatCard label="Delivered" value={stats?.delivered??0} icon="✅"
            sub="All time" color="var(--accent-green)" />
          <StatCard label="Rebook Alerts" value={rebook.length} icon="🔔"
            sub={rebook.length > 0 ? 'Action required' : 'All clear'}
            color={rebook.length > 0 ? 'var(--brand)' : 'var(--accent-green)'}
            onClick={() => navigate('/notifications')} />
        </div>
      )}

      {/* Bottom two-col grid — stacks on mobile */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18 }}>

        {/* Recent bookings */}
        <Card noPad className="au d2">
          <div style={{ padding:'16px 20px 0' }}>
            <CardTitle action={<Btn size="sm" variant="ghost" onClick={() => navigate('/bookings')}>View all →</Btn>}>
              Recent Bookings
            </CardTitle>
          </div>
          {bookings.length === 0 ? (
            <div style={{ padding:'36px 20px', textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No bookings yet</div>
          ) : bookings.map((b, i) => {
            const sc = STATUS_CONF[b.status] || STATUS_CONF.Pending;
            return (
              <div key={b.id} style={{
                display:'flex', alignItems:'center', gap:14, padding:'12px 20px',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                transition:'background .12s', cursor:'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
                <Avatar name={b.customer?.name} size={32} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }} className="truncate">{b.customer?.name}</div>
                  <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>{b.bookingNo} · {b.cylinderType}</div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)', marginBottom:3 }}>₹{parseFloat(b.amount).toLocaleString('en-IN')}</div>
                  <Badge type={sc.type} dot>{sc.label}</Badge>
                </div>
              </div>
            );
          })}
        </Card>

        {/* Rebook alerts */}
        <Card noPad className="au d3">
          <div style={{ padding:'16px 20px 0' }}>
            <CardTitle action={rebook.length > 0 ? <Btn size="sm" variant="ghost" onClick={() => navigate('/notifications')}>View all →</Btn> : null}>
              🔔 Rebook Alerts
            </CardTitle>
          </div>
          {rebook.length === 0 ? (
            <div style={{ padding:'36px 20px', textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:10 }}>✅</div>
              <div style={{ fontSize:13, color:'var(--text-muted)' }}>All customers up to date</div>
            </div>
          ) : rebook.slice(0, 6).map((c, i) => {
            const urgent = !c.daysSince || c.daysSince >= 45;
            return (
              <div key={c.id} style={{
                display:'flex', alignItems:'center', gap:12, padding:'11px 20px',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
              }}>
                <div style={{
                  width:8, height:8, borderRadius:'50%', flexShrink:0,
                  background: urgent ? 'var(--accent-red)' : 'var(--accent-amber)',
                  animation: urgent ? 'pulse2 1.2s infinite' : undefined,
                }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }} className="truncate">{c.name}</div>
                  <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>
                    {c.connectionNo} · {c.daysSince != null ? `${c.daysSince}d ago` : 'Never booked'}
                  </div>
                </div>
                <Btn size="xs" onClick={() => navigate('/bookings')}>Book</Btn>
              </div>
            );
          })}
          {rebook.length > 6 && (
            <div style={{ padding:'10px 20px', textAlign:'center', borderTop:'1px solid var(--border-subtle)' }}>
              <button onClick={() => navigate('/notifications')} style={{ fontSize:12, color:'var(--brand)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                +{rebook.length - 6} more — View all
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
